import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha
} from '@mui/material';
import {
  Code as CodeIcon,
  Storage as StorageIcon,
  Dns as ServerIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  IntegrationInstructions as IntegrationIcon,
  Terminal as TerminalIcon,
  SettingsInputComponent as ArchitectureIcon,
  Layers as LayersIcon
} from '@mui/icons-material';
import { GlassContainer, fadeInUp } from '../styles/sharedStyles';

const Documentation = () => {
  const theme = useTheme();

  const sections = [
    {
      title: 'Project Overview',
      icon: <LayersIcon />,
      content: 'WatchTower is a professional-grade dashboard designed for high-performance home lab environments. It provides real-time monitoring and centralized control for TrueNAS SCALE, Docker containers, and a suite of distributed services.',
      details: [
        { label: 'Core Platform', value: 'RAIDNET Enterprise Environment' },
        { label: 'Target OS', value: 'TrueNAS SCALE (Linux-based)' },
        { label: 'Hardware Base', value: 'Custom NAS + Raspberry Pi 5 Control Plane' }
      ]
    },
    {
      title: 'Tech Stack Architecture',
      icon: <ArchitectureIcon />,
      content: 'Built on a modern, asynchronous foundation to ensure high responsiveness and minimal system overhead.',
      details: [
        { label: 'Backend', value: 'Python 3.11 + FastAPI (High Performance Async)' },
        { label: 'Frontend', value: 'React 18 + Material UI v5 (Glassmorphism UI)' },
        { label: 'Real-time', value: 'WebSocket (TrueNAS) + Optimized HTTP Probes' },
        { label: 'Database', value: 'SQLite + SQLAlchemy (Async Driver)' }
      ]
    },
    {
      title: 'Network & Infrastructure',
      icon: <ServerIcon />,
      content: 'Orchestrated using Docker Compose with an automated Nginx reverse proxy layer.',
      details: [
        { label: 'Main NAS IP', value: '192.168.0.10' },
        { label: 'TrueNAS Internal', value: '192.168.8.8 (truenas.raidnet.pl)' },
        { label: 'Reverse Proxy', value: 'Nginx (Port 8090)' },
        { label: 'WOL Broadcast', value: '192.168.8.255 (MAC: 10:ff:e0:67:45:73)' }
      ]
    },
    {
      title: 'Monitoring Cycles',
      icon: <SpeedIcon />,
      content: 'Highly optimized data ingestion pipeline with tiered refresh rates.',
      details: [
        { label: 'Backend Pulse', value: '60 Seconds (Full System Audit)' },
        { label: 'Dashboard Sync', value: '30 Seconds (Frontend Refresh)' },
        { label: 'Metrics Cache', value: 'In-memory (FastAPI State)' },
        { label: '轻量 Metrics', value: 'Utilization-only sub-refresh (CPU/RAM/Net)' }
      ]
    },
    {
      title: 'Security & Access Control',
      icon: <SecurityIcon />,
      content: 'Enterprise-grade Role Based Access Control (RBAC) protecting sensitive operations.',
      details: [
        { label: 'Auth Method', value: 'JWT (JSON Web Tokens)' },
        { label: 'Protocol', value: 'HTTPS / TLS 1.3' },
        { label: 'IP Protection', value: 'Automated IP Blocking & Blacklist Management' },
        { label: 'User Scoping', value: 'Granular Tab-level Access Control' }
      ]
    },
    {
      title: 'Service Integrations',
      icon: <IntegrationIcon />,
      content: 'Unified monitoring for the entire homelab ecosystem.',
      details: [
        { label: 'Media', value: 'Immich (Photos) + Jellyfin (Video)' },
        { label: 'Automation', value: 'Home Assistant (Core integration)' },
        { label: 'Storage', value: 'Nextcloud + WebDAV + ZFS Pools' },
        { label: 'Maintenance', value: 'Docker Container Control + VM Management' }
      ]
    }
  ];

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{
            fontFamily: "'Orbitron', sans-serif",
            fontWeight: 900,
            letterSpacing: '4px',
            background: 'linear-gradient(135deg, #00d4ff 0%, #ffffff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
            mb: 2,
            animation: `${fadeInUp} 0.6s ease-out`
          }}
        >
          System Documentation
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)', maxWidth: '800px', mx: 'auto' }}>
          Technical specification and architectural overview of the WatchTower environment.
          This system provides critical visibility into the RAIDNET distributed infrastructure.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {sections.map((section, index) => (
          <Grid item xs={12} md={6} lg={4} key={section.title}>
            <GlassContainer
              sx={{
                height: '100%',
                p: 3,
                animation: `${fadeInUp} 0.6s ease-out`,
                animationDelay: `${index * 0.1}s`,
                animationFillMode: 'both',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-5px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: '12px',
                    background: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: 'flex'
                  }}
                >
                  {section.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                  {section.title}
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3, lineHeight: 1.6 }}>
                {section.content}
              </Typography>

              <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.05)' }} />

              <List sx={{ p: 0 }}>
                {section.details.map((detail) => (
                  <Box key={detail.label} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', mb: 0.5 }}>
                      {detail.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.primary.light, fontWeight: 600, fontFamily: 'monospace' }}>
                      {detail.value}
                    </Typography>
                  </Box>
                ))}
              </List>
            </GlassContainer>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Paper
          sx={{
            p: 4,
            background: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '24px',
            border: '1px dashed rgba(255, 255, 255, 0.1)',
            maxWidth: '900px',
            mx: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <TerminalIcon sx={{ fontSize: 40, color: 'rgba(255, 255, 255, 0.2)' }} />
          </Box>
          <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
            Architecture Note
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
            "The system is designed for high-availability monitoring. All backend processes are isolated in Docker containers,
            ensuring that monitoring operations do not interfere with the host system's primary storage tasks. 
            Data persistence is managed via an optimized SQLite layer, providing fast access to historical performance metrics."
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Documentation;
